// tabs/ChargesTab.jsx - Charges tab (document level)
import React, { useState } from 'react';
import { Box, Grid, Stack, TextInput, Group, Text, NumberInput, Checkbox, Paper } from '@mantine/core';

const ChargesTab = ({ quoteData, setQuoteData }) => {
  const [chargesData, setChargesData] = useState({
    documentSubTotal: 0.00,
    discountPercent: 0.0000,
    discountDollar: 0.00,
    documentSubTotalNet: 0.00,
    charges: {
      freight: { percent: 0.0000, dollar: 0.00 },
      vlMcAmx: { percent: 0.0000, dollar: 0.00 },
      packaging: { percent: 0.0000, dollar: 0.00 },
      feeRestock: { percent: 0.0000, dollar: 0.00 },
      adgFee: { percent: 0.0000, dollar: 0.00 },
      tax: { percent: 0.0000, dollar: 0.00 }
    },
    totalCharges: 0.00,
    taxSettings: {
      subTotalTaxable: { a: true, b: true, value: 0.00 },
      addToTotal: { print: true, a: true, b: false },
      taxable: { a: true, b: true },
      miscTax: 0.00,
      freight: { print: true, taxable: true, a: false, b: false, value: 0.00 },
      vlMcAmx: { print: true, taxable: true, a: false, b: false, value: 0.00 },
      packaging: { print: true, taxable: true, a: false, b: false, value: 0.00 },
      feeRestock: { print: true, taxable: true, a: false, b: false, value: 0.00 },
      adgFee: { print: true, taxable: true, a: false, b: false, value: 0.00 },
      tax: { print: true, taxable: true, a: true, b: true, value: 0.00 }
    },
    totalTax: 0.00
  });

  const handleBasicChange = (field, value) => {
    setChargesData(prev => ({ ...prev, [field]: value }));
  };

  const handleChargeChange = (charge, type, value) => {
    setChargesData(prev => ({
      ...prev,
      charges: {
        ...prev.charges,
        [charge]: { ...prev.charges[charge], [type]: value }
      }
    }));
  };

  const handleTaxSettingChange = (field, property, value) => {
    setChargesData(prev => ({
      ...prev,
      taxSettings: {
        ...prev.taxSettings,
        [field]: { ...prev.taxSettings[field], [property]: value }
      }
    }));
  };

  return (
    <Box p="md">
      <Grid gutter="md">
        {/* Left Side - Main Charges */}
        <Grid.Col span={8}>
          <Stack spacing="xs">
            {/* Document Totals */}
            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500} w={150} ta="right">Document SubTotal:</Text>
              <NumberInput
                value={chargesData.documentSubTotal}
                variant="filled"
                size="sm"
                w={80}
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={2}
                fixedDecimalScale
                onChange={(value) => handleBasicChange('documentSubTotal', value)}
              />
              <Text size="sm" fw={500}>$</Text>
            </Group>

            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500} w={150} ta="right">Discount (%):</Text>
              <NumberInput
                value={chargesData.discountPercent}
                variant="filled"
                size="sm"
                w={80}
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={4}
                fixedDecimalScale
                onChange={(value) => handleBasicChange('discountPercent', value)}
              />
              <Text size="sm" fw={500}>%</Text>
            </Group>

            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500} w={150} ta="right">Discount ($):</Text>
              <NumberInput
                value={chargesData.discountDollar}
                variant="filled"
                size="sm"
                w={80}
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={2}
                fixedDecimalScale
                onChange={(value) => handleBasicChange('discountDollar', value)}
              />
              <Text size="sm" fw={500}>$</Text>
            </Group>

            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500} w={150} ta="right">Document SubTotal Net:</Text>
              <NumberInput
                value={chargesData.documentSubTotalNet}
                variant="filled"
                size="sm"
                w={80}
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={2}
                fixedDecimalScale
                onChange={(value) => handleBasicChange('documentSubTotalNet', value)}
              />
              <Text size="sm" fw={500}>$</Text>
            </Group>

            <Box mt="md">
              {/* Individual Charges */}
              {Object.entries(chargesData.charges).map(([chargeKey, charge]) => (
                <Group key={chargeKey} spacing="xs" align="flex-end" mb="xs">
                  <Text size="sm" fw={500} w={150} ta="right">
                    {chargeKey === 'vlMcAmx' ? 'V1/MC/AMX' :
                     chargeKey === 'feeRestock' ? 'T Fee / Restock' :
                     chargeKey === 'adgFee' ? 'ADG FEE' :
                     chargeKey.charAt(0).toUpperCase() + chargeKey.slice(1)}:
                  </Text>
                  <NumberInput
                    value={charge.percent}
                    variant="filled"
                    size="sm"
                    w={80}
                    styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                    decimalScale={4}
                    fixedDecimalScale
                    onChange={(value) => handleChargeChange(chargeKey, 'percent', value)}
                  />
                  <Text size="sm" fw={500}>%</Text>
                  <NumberInput
                    value={charge.dollar}
                    variant="filled"
                    size="sm"
                    w={80}
                    styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                    decimalScale={2}
                    fixedDecimalScale
                    onChange={(value) => handleChargeChange(chargeKey, 'dollar', value)}
                  />
                  <Text size="sm" fw={500}>$</Text>
                </Group>
              ))}
            </Box>

            <Group spacing="xs" align="flex-end" mt="md">
              <Text size="sm" fw={500} w={150} ta="right">Total Charges:</Text>
              <NumberInput
                value={chargesData.totalCharges}
                variant="filled"
                size="sm"
                w={80}
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={2}
                fixedDecimalScale
                onChange={(value) => handleBasicChange('totalCharges', value)}
              />
            </Group>
          </Stack>
        </Grid.Col>

        {/* Right Side - Tax Settings */}
        <Grid.Col span={4}>
          <Stack spacing="md">
            {/* Tax Headers */}
            <Group spacing="xs" justify="center">
              <Text size="sm" fw={500} w={20} ta="center">A</Text>
              <Text size="sm" fw={500} w={20} ta="center">B</Text>
              <Text size="sm" fw={500} w={80} ta="center">Sub Total Tax</Text>
            </Group>

            {/* SubTotal Taxable */}
            <Group spacing="xs" align="center">
              <Text size="sm" fw={500} w={100} ta="right">SubTotal Taxable:</Text>
              <Checkbox
                checked={chargesData.taxSettings.subTotalTaxable.a}
                onChange={(e) => handleTaxSettingChange('subTotalTaxable', 'a', e.currentTarget.checked)}
              />
              <Checkbox
                checked={chargesData.taxSettings.subTotalTaxable.b}
                onChange={(e) => handleTaxSettingChange('subTotalTaxable', 'b', e.currentTarget.checked)}
              />
              <NumberInput
                value={chargesData.taxSettings.subTotalTaxable.value}
                variant="filled"
                size="sm"
                w={80}
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={2}
                fixedDecimalScale
                onChange={(value) => handleTaxSettingChange('subTotalTaxable', 'value', value)}
              />
            </Group>

            {/* Add to Total */}
            <Group spacing="xs" align="center">
              <Text size="sm" fw={500} w={60} ta="center">Add to Total</Text>
              <Text size="sm" fw={500} w={40} ta="center">Print</Text>
              <Text size="sm" fw={500} w={20} ta="center">A</Text>
              <Text size="sm" fw={500} w={20} ta="center">B</Text>
              <Text size="sm" fw={500} w={60} ta="center">Taxable</Text>
              <Text size="sm" fw={500} w={60} ta="center">Misc Tax</Text>
            </Group>

            <Group spacing="xs" align="center">
              <Checkbox
                checked={chargesData.taxSettings.addToTotal.print}
                onChange={(e) => handleTaxSettingChange('addToTotal', 'print', e.currentTarget.checked)}
              />
              <Checkbox
                checked={chargesData.taxSettings.addToTotal.a}
                onChange={(e) => handleTaxSettingChange('addToTotal', 'a', e.currentTarget.checked)}
              />
              <Checkbox
                checked={chargesData.taxSettings.addToTotal.b}
                onChange={(e) => handleTaxSettingChange('addToTotal', 'b', e.currentTarget.checked)}
              />
              <NumberInput
                value={chargesData.taxSettings.miscTax}
                variant="filled"
                size="sm"
                w={80}
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={2}
                fixedDecimalScale
                onChange={(value) => handleBasicChange('miscTax', value)}
              />
              <Text size="sm" fw={500}>$</Text>
            </Group>

            {/* Individual Tax Settings */}
            {Object.entries(chargesData.taxSettings).filter(([key]) => 
              ['freight', 'vlMcAmx', 'packaging', 'feeRestock', 'adgFee', 'tax'].includes(key)
            ).map(([key, settings]) => (
              <Group key={key} spacing="xs" align="center">
                <Checkbox
                  checked={settings.print}
                  onChange={(e) => handleTaxSettingChange(key, 'print', e.currentTarget.checked)}
                />
                <Checkbox
                  checked={settings.taxable}
                  onChange={(e) => handleTaxSettingChange(key, 'taxable', e.currentTarget.checked)}
                />
                <Checkbox
                  checked={settings.a}
                  onChange={(e) => handleTaxSettingChange(key, 'a', e.currentTarget.checked)}
                />
                <Checkbox
                  checked={settings.b}
                  onChange={(e) => handleTaxSettingChange(key, 'b', e.currentTarget.checked)}
                />
                <NumberInput
                  value={settings.value}
                  variant="filled"
                  size="sm"
                  w={80}
                  styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                  decimalScale={2}
                  fixedDecimalScale
                  onChange={(value) => handleTaxSettingChange(key, 'value', value)}
                />
                <Text size="sm" fw={500}>$</Text>
              </Group>
            ))}

            <Group spacing="xs" align="flex-end" mt="md">
              <Text size="sm" fw={500} w={80} ta="right">Total Tax:</Text>
              <NumberInput
                value={chargesData.totalTax}
                variant="filled"
                size="sm"
                w={80}
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={2}
                fixedDecimalScale
                onChange={(value) => handleBasicChange('totalTax', value)}
              />
              <Text size="sm" fw={500}>$</Text>
            </Group>
          </Stack>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default ChargesTab;